import { useState, ReactNode } from 'react';
import { Loader2, Plus, Music2, Image as ImageIcon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useToast } from '@/hooks/useToast';
import { PLAYLIST_KIND, generateRandomGradient } from '@/lib/musicTypes';

interface CreatePlaylistDialogProps {
  onSuccess?: () => void;
  children?: ReactNode;
}

export function CreatePlaylistDialog({ onSuccess, children }: CreatePlaylistDialogProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [gradient, setGradient] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(true);
  const [isCollaborative, setIsCollaborative] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { user } = useCurrentUser();
  const { mutate: createEvent, isPending } = useNostrPublish();
  const { mutateAsync: uploadFile } = useUploadFile();
  const { toast } = useToast();

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl('');
      setGradient([]);
    }
  };

  const handleRandomizeGradient = () => {
    setGradient(generateRandomGradient());
    setImageFile(null);
    setImageUrl('');
  };

  // Generate preview style
  const previewStyle = imageFile
    ? { backgroundImage: `url(${URL.createObjectURL(imageFile)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : imageUrl
    ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : gradient.length > 0
    ? { background: `linear-gradient(135deg, ${gradient.join(', ')})` }
    : null;

  const handleSubmit = async () => {
    console.log('CreatePlaylistDialog: handleSubmit called');
    console.log('CreatePlaylistDialog: user =', user);
    console.log('CreatePlaylistDialog: title =', title);

    if (!user) {
      console.log('CreatePlaylistDialog: User not logged in, showing toast');
      toast({
        title: 'Not logged in',
        description: 'Please log in to create a playlist',
        variant: 'destructive',
      });
      return;
    }

    if (!title.trim()) {
      console.log('CreatePlaylistDialog: Title is empty, showing toast');
      toast({
        title: 'Error',
        description: 'Please enter a playlist title',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);

    try {
      // Upload image if provided
      let finalImageUrl = imageUrl;
      if (imageFile) {
        console.log('CreatePlaylistDialog: Starting image upload');
        toast({ title: 'Uploading cover image...' });
        const [[, imgUrl]] = await uploadFile(imageFile);
        finalImageUrl = imgUrl;
        console.log('CreatePlaylistDialog: Image uploaded to:', finalImageUrl);
      }

      // Generate a unique identifier from title and timestamp
      const d = `${title.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

      const tags: string[][] = [
        ['d', d],
        ['title', title.trim()],
        ['t', 'playlist'],
        ['alt', `Playlist: ${title.trim()}`],
      ];

      if (description.trim()) {
        tags.push(['description', description.trim()]);
      }

      if (finalImageUrl) {
        tags.push(['image', finalImageUrl]);
      }

      if (isPublic) {
        tags.push(['public', 'true']);
      }

      if (isCollaborative) {
        tags.push(['collaborative', 'true']);
      }

      console.log('CreatePlaylistDialog: About to call createEvent with:', { kind: PLAYLIST_KIND, content: description.trim(), tags });

      createEvent(
        {
          kind: PLAYLIST_KIND,
          content: description.trim(),
          tags,
        },
        {
          onSuccess: () => {
            toast({
              title: 'Success',
              description: 'Playlist created successfully',
            });
            setOpen(false);
            setTitle('');
            setDescription('');
            setImageFile(null);
            setImageUrl('');
            setGradient([]);
            setIsPublic(true);
            setIsCollaborative(false);
            setIsUploading(false);
            onSuccess?.();
          },
          onError: (error) => {
            toast({
              title: 'Error',
              description: error.message || 'Failed to create playlist',
              variant: 'destructive',
            });
            setIsUploading(false);
          },
        }
      );
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload image',
        variant: 'destructive',
      });
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || (
          <Button disabled={!user}>
            <Plus className="mr-2 h-4 w-4" />
            Create Playlist
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Playlist</DialogTitle>
          <DialogDescription>
            Create a new playlist to organize your favorite tracks.
          </DialogDescription>
        </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="My Awesome Playlist"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isPending || isUploading}
                maxLength={100}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="A collection of my favorite tracks..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isPending || isUploading}
                maxLength={500}
                rows={3}
              />
            </div>

            {/* Cover Image */}
            <div className="space-y-2">
              <Label>Cover Image</Label>
              <div className="flex gap-4">
                {/* Preview */}
                <div className="flex-shrink-0">
                  {previewStyle ? (
                    <div
                      className="w-32 h-32 rounded-lg bg-cover bg-center"
                      style={previewStyle}
                    >
                      {!imageFile && !imageUrl && gradient.length > 0 && (
                        <div className="h-full flex items-center justify-center">
                          <Music2 className="h-8 w-8 text-white/80" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-lg border border-dashed border-white/[0.11] bg-white/[0.11] flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex-1 space-y-3">
                  {/* Upload options */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 gap-2"
                      onClick={() => document.getElementById('playlist-image')?.click()}
                      disabled={isPending || isUploading}
                    >
                      <ImageIcon className="h-4 w-4" />
                      Upload Image
                    </Button>
                    <Input
                      id="playlist-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2"
                      onClick={handleRandomizeGradient}
                      disabled={isPending || isUploading}
                    >
                      <Sparkles className="h-4 w-4" />
                      Random
                    </Button>
                  </div>

                  {/* Or URL */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or</span>
                    </div>
                  </div>

                  <Input
                    placeholder="Paste image URL"
                    value={imageUrl}
                    onChange={(e) => {
                      setImageUrl(e.target.value);
                      setImageFile(null);
                      setGradient([]);
                    }}
                    disabled={isPending || isUploading}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="public">Public Playlist</Label>
                <p className="text-sm text-muted-foreground">
                  Allow others to discover this playlist
                </p>
              </div>
              <Switch
                id="public"
                checked={isPublic}
                onCheckedChange={setIsPublic}
                disabled={isPending || isUploading}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="collaborative">Collaborative</Label>
                <p className="text-sm text-muted-foreground">
                  Allow others to add tracks
                </p>
              </div>
              <Switch
                id="collaborative"
                checked={isCollaborative}
                onCheckedChange={setIsCollaborative}
                disabled={isPending || isUploading}
              />
            </div>
          </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isPending || isUploading}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending || isUploading || !user}>
            {(isPending || isUploading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Playlist
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
